// Custom location & address validation helpers — extracted from legacy-utils.ts

import { hasValue, hasNumericValue } from './validation';
import { indexOfValueInArrayByProperty, deepCopyProperties } from './arrays';
import { renderImperativeModal } from './imperative-modal';
import { App } from '@/services/app-compat';

const _w = window as any;

type CompanyMapCenter = { lat?: number; lng?: number };

const companyCenterCache = new Map<number, CompanyMapCenter>();

function fetchCompanyMapCenter(companyId: number | undefined): Promise<CompanyMapCenter> {
  return new Promise((resolve) => {
    if (!companyId || !Number.isFinite(companyId) || companyId <= 0) {
      resolve({});
      return;
    }
    const cached = companyCenterCache.get(companyId);
    if (cached) { resolve(cached); return; }
    try {
      const api: any = (window as any).api;
      if (!api?.getCompanyMapCenter) { resolve({}); return; }
      api.getCompanyMapCenter(
        companyId,
        (result: any) => {
          const center: CompanyMapCenter = {};
          if (result && typeof result.lat === 'number' && typeof result.lng === 'number') {
            center.lat = result.lat;
            center.lng = result.lng;
            // Only cache when we got real coords. Caching empty results would pin the failure
            // for the whole page lifetime — a later HQ-geocode (e.g. admin saves Company Profile)
            // would never be picked up without a full reload.
            companyCenterCache.set(companyId, center);
          }
          resolve(center);
        },
        () => { resolve({}); }
      );
    } catch {
      resolve({});
    }
  });
}

/**
 * Append the canonical country suffix to a Geocodio-formatted address so it matches the
 * "..., USA" / "..., Canada" trailing convention Google's geocoder always produces. Geocodio
 * omits the country in formatted_address; we add it back at the frontend translation boundary so
 * pilot saves look identical to legacy saves, and consistent with the existing data in the DB.
 *
 * Idempotent: a string that already ends with the suffix is returned unchanged. Returns the
 * input unchanged when the country code is missing or not US/CA (Geocodio only supports those).
 */
/**
 * Convert the backend's single-object GeocodeAddressComponents shape
 *   { number, street, city, state, zip, country }
 * into Google's address_components array form
 *   [{ long_name, short_name, types: [...] }, ...]
 * so the synthesized dropped-pin result mimics a real Google geocoder result and
 * downstream {@code fillInAddressFromSuggestions} populates city/state/zip/country
 * columns instead of clearing them. Empty input returns an empty array.
 */
export function backendComponentsToGoogleShape(c: any): any[] {
  if (!c) return [];
  const out: any[] = [];
  if (c.number) out.push({ long_name: c.number, short_name: c.number, types: ['street_number'] });
  if (c.street) out.push({ long_name: c.street, short_name: c.street, types: ['route'] });
  if (c.city) out.push({ long_name: c.city, short_name: c.city, types: ['locality', 'political'] });
  if (c.state) out.push({ long_name: c.state, short_name: c.state, types: ['administrative_area_level_1', 'political'] });
  if (c.zip) out.push({ long_name: c.zip, short_name: c.zip, types: ['postal_code'] });
  if (c.country) out.push({ long_name: c.country, short_name: c.country, types: ['country', 'political'] });
  return out;
}

/**
 * Extract the 2-letter country code from a Google-shape geocoder result. Looks up the
 * address_components entry whose `types` include 'country' and returns its `short_name`
 * (e.g., 'US', 'CA'). Returns undefined if absent or malformed.
 */
export function extractCountryCode(googleShapeResult: any): string | undefined {
  const components = googleShapeResult?.address_components;
  if (!Array.isArray(components)) return undefined;
  const countryComp = components.find((c: any) => Array.isArray(c?.types) && c.types.indexOf('country') >= 0);
  const code = countryComp?.short_name;
  return typeof code === 'string' && code.length === 2 ? code.toUpperCase() : undefined;
}

export function appendCountrySuffix(formatted: string | null | undefined, countryCode: string | null | undefined): string {
  const base = formatted ?? '';
  if (!base) return base;
  if (!countryCode) return base;
  const code = String(countryCode).toUpperCase();
  const suffix = (code === 'US') ? ', USA' : (code === 'CA') ? ', Canada' : null;
  if (!suffix) return base;
  if (base.endsWith(suffix)) return base;
  return base + suffix;
}

export function showLoadScreenIfParamTrue(showLoadScreen: boolean): void {
  const load = _w.load;
  if (showLoadScreen && load) {
    load.showLoadingScreen();
  }
}

export function hideLoadScreenIfParamTrue(hideLoadScreen: boolean): void {
  const load = _w.load;
  if (hideLoadScreen && load) {
    load.hideLoadingScreen();
  }
}

export function realtimeCustomLocationUpdate(): void {
  getCustomLocationListPromise(true);
}

export function getCustomLocationListPromise(overrideCache = false): Promise<any> {
  return new Promise((resolve, reject) => {
    if (hasValue(App.customLocationList) && !overrideCache) {
      resolve(deepCopyProperties(App.customLocationList));
    } else {
      (window as any).api.listCustomLocations(true, (result: any) => {
        App.customLocationList = result;
        resolve(deepCopyProperties(result));
      }, (errorResult: any) => {
        reject(errorResult);
        App.showPopupNotification("Error getting list of custom locations.", 'error');
      });
    }
  });
}

export function wrapCustomLocationOrCoordinatesInPromise(customLocationOrCoordinates: any): Promise<any> {
  return new Promise((resolve) => {
    resolve(customLocationOrCoordinates);
  });
}

export function getCustomLocationOrCoordinatesFromAddressPromise(locationVal: string): Promise<any> {
  return getCustomLocationListPromise().then((listResponse: { locations: Array<{ id: number, location: string, latitude: number, longitude: number }> }) => {
    if (!hasValue(locationVal)) {
      return wrapCustomLocationOrCoordinatesInPromise({});
    }
    for (const customLocation of listResponse.locations) {
      if (locationVal === customLocation.location) {
        return wrapCustomLocationOrCoordinatesInPromise({ type: _w.CustomLocationOrCoordinatesType.CUSTOM_LOCATION, id: customLocation.id, location: customLocation.location, latitude: customLocation.latitude, longitude: customLocation.longitude });
      }
    }
    const locationValCoordinatesArr = locationVal.split(",");
    if (hasValue(locationValCoordinatesArr) && locationValCoordinatesArr.length === 2) {
      const locationValLatitude = parseFloat(locationValCoordinatesArr[0]);
      const locationValLongitude = parseFloat(locationValCoordinatesArr[1]);
      for (const customLocation of listResponse.locations) {
        if (Math.abs(locationValLatitude - customLocation.latitude) <= 0.00002 &&
          Math.abs(locationValLongitude - customLocation.longitude) <= 0.00002) {
          return wrapCustomLocationOrCoordinatesInPromise({ type: _w.CustomLocationOrCoordinatesType.CUSTOM_LOCATION, id: customLocation.id, location: customLocation.location, latitude: locationValLatitude, longitude: locationValLongitude });
        }
      }
      if (hasNumericValue(locationValLatitude) && hasNumericValue(locationValLongitude)) {
        return wrapCustomLocationOrCoordinatesInPromise({ type: _w.CustomLocationOrCoordinatesType.COORDINATES, location: `${locationValLatitude},${locationValLongitude}`, latitude: locationValLatitude, longitude: locationValLongitude });
      }
    }
    return wrapCustomLocationOrCoordinatesInPromise({});
  });
}

// ── Trusted client / site location lookup ─────────────────────────────────
//
// Parallel to the custom-location bypass above: a stored client or site coordinate is trusted
// only when its provenance is HIGH (Geocodio high-confidence at save) or MANUAL (a human-placed
// pin). LOW / LEGACY / null coordinates are never trusted here — they fall through to geocoding
// exactly like today, so nothing regresses. See CLIENT_LOCATION_TRUST_PLAN §4.B.
//
// The lookup follows the location-field TEXT (owner decision §4.B.3): exact string equality
// against locFormattedAddress (clients) / location (sites), same semantics as the custom-location
// check — NOT fuzzy. On a match, the coordinate follows whichever record owns the string.

type TrustedLocationRecord = {
  source: 'CLIENT' | 'SITE';
  entityId: number;
  clientId: number | null; // owning client — used for the tie-break; null for orphan sites
  address: string;
  latitude: number;
  longitude: number;
};

// Session cache keyed by company id. No realtime event exists for client/site edits (unlike
// custom locations, which bust on company:customLocationUpdate), so this is a page-session cache
// with a manual invalidation export. Client/site addresses are edited on other screens; a
// call-card session is short-lived, so a stale window here is acceptable and low-risk.
const trustedLocationCache = new Map<number, Promise<TrustedLocationRecord[]>>();

/** Invalidate the trusted client/site location cache (all companies, or one). Exposed for
 *  future wiring to a realtime client/site update event; not currently triggered automatically. */
export function invalidateTrustedLocationCache(companyId?: number): void {
  if (companyId == null) {
    trustedLocationCache.clear();
  } else {
    trustedLocationCache.delete(companyId);
  }
}

function isTrustedStatus(status: any): boolean {
  return status === 'HIGH' || status === 'MANUAL';
}

function isTrustableRecord(latitude: any, longitude: any, status: any): boolean {
  return isTrustedStatus(status) && hasNumericValue(latitude) && hasNumericValue(longitude);
}

/** Build the company-wide list of trusted client + site records from the client list and
 *  all-sites list APIs. Lazily cached per company (see trustedLocationCache). Mirrors the
 *  getCustomLocationListPromise caching approach: a rejected/empty fetch resolves to [] so the
 *  send path never blocks on a lookup failure — the call still goes out via geocoding. */
function getTrustedLocationListPromise(companyId: number | undefined): Promise<TrustedLocationRecord[]> {
  if (!companyId || !Number.isFinite(companyId) || companyId <= 0) {
    return Promise.resolve([]);
  }
  // Both list endpoints below are scoped to the SESSION's company — they take no companyId and
  // read it from the token. For a cross-company dispatcher working an officer company's call,
  // fetching them would return the dispatcher's own clients and file them in this cache under the
  // other company's id, so an address that happens to match one of their own records would place
  // the pin at their client's coordinate. There is no endpoint that answers this for another
  // company, so the honest answer is no trusted records rather than the wrong company's.
  const sessionCompanyId = Number((window as any).getCurrentCompanyId?.());
  if (Number.isFinite(sessionCompanyId) && sessionCompanyId !== companyId) {
    return Promise.resolve([]);
  }
  const cached = trustedLocationCache.get(companyId);
  if (cached) return cached;

  const api: any = (window as any).api;

  const clientsPromise = new Promise<TrustedLocationRecord[]>((resolve) => {
    if (!api?.listClientGroup) { resolve([]); return; }
    api.listClientGroup((result: any) => {
      const out: TrustedLocationRecord[] = [];
      const clients = result?.npsdClientList ?? [];
      for (const c of clients) {
        if (c && typeof c.locFormattedAddress === 'string' &&
            isTrustableRecord(c.latitude, c.longitude, c.addressVerificationStatus)) {
          out.push({ source: 'CLIENT', entityId: c.id, clientId: c.id, address: c.locFormattedAddress, latitude: c.latitude, longitude: c.longitude });
        }
      }
      resolve(out);
    }, () => { resolve([]); });
  });

  const sitesPromise = new Promise<TrustedLocationRecord[]>((resolve) => {
    if (!api?.listAllClientGroupSite) { resolve([]); return; }
    api.listAllClientGroupSite((result: any[]) => {
      const out: TrustedLocationRecord[] = [];
      const sites = result ?? [];
      for (const s of sites) {
        if (s && typeof s.location === 'string' &&
            isTrustableRecord(s.latitude, s.longitude, s.addressVerificationStatus)) {
          out.push({ source: 'SITE', entityId: s.id, clientId: hasValue(s.npsdClientId) ? s.npsdClientId : null, address: s.location, latitude: s.latitude, longitude: s.longitude });
        }
      }
      resolve(out);
    }, () => { resolve([]); });
  });

  const combined = Promise.all([clientsPromise, sitesPromise]).then(([clients, sites]) => [...clients, ...sites]);
  trustedLocationCache.set(companyId, combined);
  return combined;
}

/**
 * Resolve the typed location text against trusted client/site records for the company.
 * Exact string equality (custom-location semantics). Only HIGH/MANUAL records with numeric
 * coords participate. Tie-break when a string matches multiple trusted records: prefer a record
 * linked to the client selected on the form, then site over client (more specific).
 *
 * Resolves with the same CUSTOM_LOCATION result shape the custom-location branch uses (so
 * fillInAddressFromSuggestions keeps the address text verbatim and attaches the coords), tagged
 * with trustedSource/entityId for Workstream C's write-back context. Resolves null on no match.
 */
export function getTrustedLocationFromAddressPromise(locationVal: string, companyId: number | undefined, selectedClientId: number | null): Promise<any> {
  if (!hasValue(locationVal)) return Promise.resolve(null);
  return getTrustedLocationListPromise(companyId).then((records) => {
    const matches = records.filter((r) => r.address === locationVal);
    if (matches.length === 0) return null;

    let chosen = matches[0];
    if (matches.length > 1) {
      const clientLinked = (selectedClientId != null)
        ? matches.filter((r) => r.clientId === selectedClientId)
        : [];
      const pool = clientLinked.length > 0 ? clientLinked : matches;
      // Prefer site over client (more specific) within the chosen pool.
      chosen = pool.find((r) => r.source === 'SITE') ?? pool[0];
    }

    return {
      type: _w.CustomLocationOrCoordinatesType.CUSTOM_LOCATION,
      // No id: this is not a custom location. fillInCustomLocationAndCoordinatesAddress nulls
      // customLocationId when id is absent, which is correct — the coordinate provenance is the
      // client/site record, carried via the trustedSource tag below.
      location: chosen.address,
      latitude: chosen.latitude,
      longitude: chosen.longitude,
      trustedSource: chosen.source,
      trustedEntityId: chosen.entityId,
    };
  });
}

// ── Write-back target lookup (Workstream C) ───────────────────────────────
//
// When the Verify Address modal is invoked for text that matches a KNOWN client/site whose stored
// coordinate is NOT yet trusted (status LOW/LEGACY/null), and the dispatcher corrects the pin, we
// offer to save that pin back to the record. This lookup finds that untrusted match — the mirror
// image of the trusted lookup above. Prefers a record linked to the client selected on the form,
// then site over client (more specific), matching the trusted tie-break.

export interface WriteBackTarget {
  type: 'CLIENT' | 'SITE';
  id: number;
  name: string;
}

function getWriteBackTargetForAddress(locationVal: string, companyId: number | undefined, selectedClientId: number | null): Promise<WriteBackTarget | undefined> {
  if (!hasValue(locationVal)) return Promise.resolve(undefined);
  const api: any = (window as any).api;

  const clientsPromise = new Promise<Array<WriteBackTarget & { clientId: number | null }>>((resolve) => {
    if (!api?.listClientGroup) { resolve([]); return; }
    api.listClientGroup((result: any) => {
      const out: Array<WriteBackTarget & { clientId: number | null }> = [];
      const clients = result?.npsdClientList ?? [];
      for (const c of clients) {
        // Untrusted = present address text but no trusted coordinate provenance.
        if (c && typeof c.locFormattedAddress === 'string' && c.locFormattedAddress === locationVal
            && !isTrustedStatus(c.addressVerificationStatus)) {
          out.push({ type: 'CLIENT', id: c.id, name: c.name ?? c.locFormattedAddress, clientId: c.id });
        }
      }
      resolve(out);
    }, () => { resolve([]); });
  });

  const sitesPromise = new Promise<Array<WriteBackTarget & { clientId: number | null }>>((resolve) => {
    if (!api?.listAllClientGroupSite) { resolve([]); return; }
    api.listAllClientGroupSite((result: any[]) => {
      const out: Array<WriteBackTarget & { clientId: number | null }> = [];
      const sites = result ?? [];
      for (const s of sites) {
        if (s && typeof s.location === 'string' && s.location === locationVal
            && !isTrustedStatus(s.addressVerificationStatus)) {
          out.push({ type: 'SITE', id: s.id, name: s.siteName ?? s.location, clientId: hasValue(s.npsdClientId) ? s.npsdClientId : null });
        }
      }
      resolve(out);
    }, () => { resolve([]); });
  });

  return Promise.all([clientsPromise, sitesPromise]).then(([clients, sites]) => {
    const matches = [...clients, ...sites];
    if (matches.length === 0) return undefined;
    const clientLinked = (selectedClientId != null)
      ? matches.filter((r) => r.clientId === selectedClientId)
      : [];
    const pool = clientLinked.length > 0 ? clientLinked : matches;
    const chosen = pool.find((r) => r.type === 'SITE') ?? pool[0];
    return { type: chosen.type, id: chosen.id, name: chosen.name };
  }).catch(() => undefined);
}

/** Fire a write-back of a corrected pin to the matching client/site record (provenance MANUAL).
 *  Non-blocking: called after the call is already resolving, so a failure surfaces one quiet
 *  notice and never affects the call. Invalidates the trusted-location cache on success so the
 *  next lookup sees the new coordinate. */
function fireLocationPinWriteBack(target: WriteBackTarget, lat: number, lng: number, companyId: number | undefined): void {
  const api: any = (window as any).api;
  const onErr = () => {
    App.showPopupNotification('The location was used for this call but could not be saved to the record.', 'warning');
  };
  const onOk = () => { invalidateTrustedLocationCache(companyId); };
  try {
    if (target.type === 'CLIENT') {
      if (!api?.writeBackClientLocationPin) return;
      api.writeBackClientLocationPin(target.id, lat, lng, onOk, onErr);
    } else {
      if (!api?.writeBackSiteLocationPin) return;
      api.writeBackSiteLocationPin(target.id, lat, lng, onOk, onErr);
    }
  } catch { onErr(); }
}

/**
 * Show the GeocodePreview to let the dispatcher confirm or correct a geocode result.
 *
 * Replaces the older AddressSelectionModal: the preview supports the multi-result picker case
 * (pass each candidate as an alternate) AND drag-to-adjust AND no-result drop-pin AND
 * discrepancy-on-edit confirmation. One component, four modes.
 *
 * Resolves with the user's chosen location data:
 *   {
 *     lat, lng,
 *     formatted,
 *     alternateIndex: number | null     // -1 if user dragged; >=0 if they picked an alternate
 *   }
 *
 * Rejects with an Error('preview-cancelled') if the user cancels.
 */
export function showGeocodePreview(opts: {
    mode: 'low-confidence' | 'no-result' | 'discrepancy';
    formatted?: string;
    lat?: number;
    lng?: number;
    accuracyType?: string;
    country?: string;
    /** Original typed address text — prefills the editable re-search field; returned by "Use as typed". */
    query?: string;
    alternates?: Array<{ formatted: string; lat: number; lng: number; accuracyType?: string }>;
    previousLat?: number;
    previousLng?: number;
    fallbackCenterLat?: number;
    fallbackCenterLng?: number;
    requireConfirm?: boolean;
    /** Known client/site the typed address belongs to, offered for write-back on a corrected pin. */
    writeBackTarget?: { type: 'CLIENT' | 'SITE'; id: number; name: string };
    /** Whether the write-back offer may show at all (false in discrepancy mode). */
    canWriteBack?: boolean;
}): Promise<{ lat: number; lng: number; formatted: string; alternateIndex: number; reverted: boolean; components?: any; useAsTyped?: boolean; writeBack?: boolean }> {
    return new Promise((resolve, reject) => {
        const handle = renderImperativeModal((window as any).GeocodePreviewReact, {
            open: true,
            mode: opts.mode,
            formatted: opts.formatted,
            lat: opts.lat,
            lng: opts.lng,
            accuracyType: opts.accuracyType,
            country: opts.country,
            query: opts.query,
            alternates: opts.alternates ?? [],
            previousLat: opts.previousLat,
            previousLng: opts.previousLng,
            fallbackCenterLat: opts.fallbackCenterLat,
            fallbackCenterLng: opts.fallbackCenterLng,
            requireConfirm: opts.requireConfirm ?? false,
            writeBackTarget: opts.writeBackTarget,
            canWriteBack: opts.canWriteBack ?? false,
            onAccept: (lat: number, lng: number, formatted: string, alternate?: any, components?: any, extra?: { useAsTyped?: boolean; writeBack?: boolean }) => {
                let alternateIndex = -1;
                if (alternate && opts.alternates) {
                    alternateIndex = opts.alternates.findIndex(
                        (a) => a.lat === alternate.lat && a.lng === alternate.lng
                    );
                }
                resolve({ lat, lng, formatted, alternateIndex, reverted: false, components, useAsTyped: extra?.useAsTyped === true, writeBack: extra?.writeBack === true });
                handle.destroy();
            },
            onRevert: () => {
                resolve({
                    lat: opts.previousLat ?? 0,
                    lng: opts.previousLng ?? 0,
                    formatted: '',
                    alternateIndex: -1,
                    reverted: true,
                });
                handle.destroy();
            },
            onCancel: () => {
                reject(new Error('preview-cancelled'));
                handle.destroy();
            },
        });
    });
}

/**
 * @deprecated Retained for backward compatibility. New code should use {@link showGeocodePreview}.
 */
export function showAddressSelectionModal(addresses: string[]): Promise<number> {
  return new Promise((resolve, reject) => {
    const handle = renderImperativeModal(
      (window as any).AddressSelectionModalReact,
      {
        open: true,
        addresses: addresses,
        onSelect: function (index: number) {
          resolve(index);
          handle.destroy();
        },
        onCancel: function () {
          reject(new Error('address-selection-cancelled'));
          handle.destroy();
        }
      }
    );
  });
}

/**
 * Resolve the location string from params.
 * Prefers params.address (string), falls back to params.locationField.value,
 * then DOM query for #txtValidateableLocation.
 */
function resolveLocationValue(params: any): string {
  if (typeof params.address === 'string') return params.address;
  if (hasValue(params.locationField)) return params.locationField.value;
  const el = document.getElementById('txtValidateableLocation') as HTMLInputElement | null;
  return el ? el.value : '';
}

export function validateAddressWithSuggestions(params: any): Promise<any> {
  const locationVal = resolveLocationValue(params);
  const showLoadingScreen: boolean = (params.showLoadingScreen != null) ? params.showLoadingScreen : false;

  return getCustomLocationOrCoordinatesFromAddressPromise(locationVal).then((customLocationOrCoordinates: any) => {
    if (customLocationOrCoordinates.type === _w.CustomLocationOrCoordinatesType.CUSTOM_LOCATION || customLocationOrCoordinates.type === _w.CustomLocationOrCoordinatesType.COORDINATES) {
      hideLoadScreenIfParamTrue(showLoadingScreen);
      return customLocationOrCoordinates;
    }
    // Trusted client/site coordinate: the location text exactly matches a HIGH/MANUAL client or
    // site record. Resolve with stored coords, verbatim text, no geocode — same as the custom
    // location branch. Falls through to geocoding on no match.
    return getTrustedLocationFromAddressPromise(locationVal, params.companyId, params.selectedClientId ?? null).then((trusted: any) => {
      if (trusted) {
        hideLoadScreenIfParamTrue(showLoadingScreen);
        return trusted;
      }
      return validateAddressWithSuggestionsFromGeocoding(params);
    });
  });
}

function sendGeocodeSample(raw: string, geocoderResult: any): void {
  try {
    const api: any = (window as any).api;
    if (!api?.sendGeocodeSample || !geocoderResult) return;
    const location = geocoderResult.geometry?.location;
    const lat: number = typeof location?.lat === 'function' ? location.lat() : location?.lat;
    const lng: number = typeof location?.lng === 'function' ? location.lng() : location?.lng;
    api.sendGeocodeSample(raw, geocoderResult.formatted_address, geocoderResult.place_id, lat, lng);
  } catch {}
}

/**
 * Build the resolution for "Use address as typed": keep the dispatcher's typed text as the saved
 * formatted address, attach the best-available coordinate (may be null), and tag the result so the
 * record lands as LOW (confidence='low', not userAdjusted) for Address Review. With no coordinate,
 * downstream fillInAddress clears the coords and the record still saves LOW.
 *
 * pinnedToArea marks a coordinate that is the company's operating-area centre rather than any
 * answer for this address; fillInAddress then stores AREA instead of LOW.
 */
function buildUseAsTypedResultsObj(typedText: string, lat: number | undefined, lng: number | undefined, pinnedToArea: boolean = false): any {
  const hasCoords = hasNumericValue(lat) && hasNumericValue(lng);
  if (hasCoords) {
    const typedResult = {
      formatted_address: typedText,
      geometry: {
        location: {
          lat: () => lat,
          lng: () => lng,
        },
      },
      // No parsed components — the typed text is kept verbatim as the formatted address and the
      // city/state/zip columns are cleared, consistent with a coordinate-first save.
      address_components: [],
    };
    return {
      geocoderResults: [typedResult],
      // 'address', NOT 'location': the whole point of use-as-typed is that the typed text
      // survives as the saved formatted address. The 'location' type would make fillInAddress
      // overwrite it with the raw "lat, lng" string; coords still flow via geometry.location.
      locationType: 'address',
      locationCoords: { lat, lng },
      confidence: 'low',
      pinnedToArea,
    };
  }
  // No coordinate at all — resolve empty so downstream clears coords; still LOW so it's reviewable.
  return {
    geocoderResults: [],
    locationType: 'address',
    locationCoords: null,
    confidence: 'low',
  };
}

export function validateAddressWithSuggestionsFromGeocoding(params: any): Promise<any> {
  const geocodeLocation = (window as any).geocodeLocation;
  const errorMessage: string = (params.errorMessage != null) ? params.errorMessage : "Error validating location. Please re-enter the location address.";
  const isLocationRequired: boolean = (params.isLocationRequired != null) ? params.isLocationRequired : true;
  const showLoadingScreen: boolean = (params.showLoadingScreen != null) ? params.showLoadingScreen : false;

  // Backend-proxy context — when the feature flag is on, these get sent to /api/geocode so
  // the server can bias the query and detect discrepancies on edits. Ignored on the legacy
  // Google path.
  const geocodeContext = {
    companyId: params.companyId,
    previousLat: params.previousLat,
    previousLng: params.previousLng,
  };

  showLoadScreenIfParamTrue(showLoadingScreen);

  const locationVal = resolveLocationValue(params);
  if (locationVal === '' && !isLocationRequired) {
    hideLoadScreenIfParamTrue(showLoadingScreen);
    return Promise.resolve({ geocoderResults: [], locationType: 'address', locationCoords: null });
  }

  // When the form opts into stricter verification (custom location creation, tour stop setup),
  // the inline preview shows even on otherwise-high-confidence results. Default is "allow-save".
  const requireConfirm: boolean = params.requireConfirm === true;

  // Call-entry flows accept low-confidence results without a preview (the dispatcher cannot
  // verify the address; the officer on scene can). Record-maintenance flows keep the preview.
  const acceptApproximate: boolean = params.acceptApproximate === true;

  return new Promise((resolve, reject) => {
    geocodeLocation(App.getGeocoder(), App.NtoS(locationVal), async function (resultsObj: any) {
      const locationResults = resultsObj.geocoderResults;
      // Defensive lowercase — backend currently returns lowercase 'high'/'low'/'none', but
      // we don't want a future normalization to uppercase to silently break the comparisons
      // below ('none' branch, 'high' branch) and downgrade every result to LOW.
      const rawConfidence = (resultsObj as any).confidence;
      const confidence: string = rawConfidence != null ? String(rawConfidence).toLowerCase() : null;
      const discrepancyFromPrevious: boolean = (resultsObj as any).discrepancyFromPrevious === true;
      const lookupUnavailable: boolean = (resultsObj as any).lookupUnavailable === true;

      if (resultsObj.locationType === 'location') {
        hideLoadScreenIfParamTrue(showLoadingScreen);
        resolve(resultsObj);
        return;
      }

      if (!hasValue(locationResults) || locationResults.length === 0) {
        hideLoadScreenIfParamTrue(showLoadingScreen);

        // Proxy-path no-result: backend explicitly said confidence='none'. Per the plan
        // (section 3.2.3), show the drop-pin preview so the dispatcher can place a marker
        // manually when they know the location but the address text isn't matchable.
        // Legacy Google path: keep the existing error-popup behavior (no backend confidence
        // signal, so we treat it as a hard failure).
        if (confidence === 'none') {
          // Call entry: nothing came back. CallCardCommand.latitude is required, so the call
          // goes out as typed, pinned to the company's operating-area centre and marked AREA.
          if (acceptApproximate && !requireConfirm) {
            const companyCenter = await fetchCompanyMapCenter(params.companyId);
            if (hasNumericValue(companyCenter?.lat) && hasNumericValue(companyCenter?.lng)) {
              // An outage is not a non-existent address: say which one happened.
              App.showPopupNotification(lookupUnavailable
                ? 'The address could not be looked up at this time. The call is pinned to the service area.'
                : 'Address not found. The call is pinned to the service area.', 'warning');
              resolve(buildUseAsTypedResultsObj(locationVal, companyCenter.lat, companyCenter.lng, true));
              return;
            }
            // No operating-area centre to fall back to — without any coordinate the call cannot
            // save, so this keeps the legacy hard-failure behavior.
            App.showPopupNotification(errorMessage, "error");
            reject(new Error(errorMessage));
            return;
          }
          try {
            const companyCenter = await fetchCompanyMapCenter(params.companyId);
            // No permission gate (owner decision 2026-07-16): ROLE_CLIENT_MANAGEMENT only exists
            // in Admin-module sessions, and this modal only appears on CAD/MDT screens — gating on
            // it made the offer unreachable. The pin write is narrow (one coordinate, MANUAL
            // provenance, audited) and the backend authorizes it like any client update.
            const canWriteBack = true;
            const writeBackTarget = await getWriteBackTargetForAddress(locationVal, params.companyId, params.selectedClientId ?? null);
            const decision = await showGeocodePreview({
              mode: 'no-result',
              formatted: undefined,
              lat: undefined,
              lng: undefined,
              alternates: [],
              query: locationVal,
              fallbackCenterLat: companyCenter.lat,
              fallbackCenterLng: companyCenter.lng,
              requireConfirm: true,
              writeBackTarget,
              canWriteBack,
            });

            if (decision.reverted) {
              reject(new Error('drop-pin-cancelled'));
              return;
            }

            // "Use address as typed" — keep the typed text, best-available coord (may be null),
            // land LOW for Address Review. The call goes out now.
            if (decision.useAsTyped) {
              resolve(buildUseAsTypedResultsObj(locationVal, decision.lat, decision.lng));
              return;
            }

            // Write-back offer: a corrected OR confirmed pin on a known-but-untrusted client/site record.
            // Fire-and-forget so a failure never blocks the call (call already resolving below).
            if (decision.writeBack && writeBackTarget && hasNumericValue(decision.lat) && hasNumericValue(decision.lng)) {
              fireLocationPinWriteBack(writeBackTarget, decision.lat, decision.lng, params.companyId);
            }

            // Convert the dropped pin into a location-type result. Prefer the reverse-geocoded
            // address text the preview resolved (decision.formatted) so the saved record carries
            // a real street address rather than raw coords. When the dispatcher toggled to "use
            // coordinates instead" in the preview, decision.components is undefined → empty
            // address_components → downstream clears the city/state/zip columns (intended:
            // dispatcher asserted the position is what matters, not the resolved address).
            // When they kept the address, components flow through so fillInAddressFromSuggestions
            // populates the city/state/zip/country columns.
            const droppedFormatted = (decision.formatted && decision.formatted.trim().length > 0)
              ? decision.formatted
              : `${decision.lat}, ${decision.lng}`;
            const droppedResult = {
              formatted_address: droppedFormatted,
              geometry: {
                location: {
                  lat: () => decision.lat,
                  lng: () => decision.lng,
                },
              },
              address_components: backendComponentsToGoogleShape((decision as any).components),
            };
            resolve({
              geocoderResults: [droppedResult],
              locationType: 'location',
              locationCoords: { lat: decision.lat, lng: decision.lng },
              userAdjusted: true,
              confidence: 'none',
            });
          } catch (e) {
            reject(e);
          }
          return;
        }

        App.showPopupNotification(errorMessage, "error");
        reject(new Error(errorMessage));
        return;
      }

      // Decide whether to show the GeocodePreview. The preview appears when:
      //   - The backend reported low-confidence (proxy path only — `confidence` field present)
      //   - The backend flagged a discrepancy from the previously-saved coordinate
      //   - Multiple Google results came back with no exact match (legacy path — replaces the
      //     old AddressSelectionModal)
      //   - The caller explicitly requires confirmation (require-confirm policy per-form)
      const top = locationResults[0];
      const topLat: number | undefined = typeof top?.geometry?.location?.lat === 'function'
          ? top.geometry.location.lat()
          : top?.geometry?.location?.lat;
      const topLng: number | undefined = typeof top?.geometry?.location?.lng === 'function'
          ? top.geometry.location.lng()
          : top?.geometry?.location?.lng;

      // The GeocodePreview only fires for proxy users. Non-proxy users continue using the
      // legacy AddressSelectionModal for multi-result disambiguation, keeping their
      // experience byte-for-byte identical to the pre-migration behavior.
      const proxyEnabled = App.useGeocodingProxy();
      const isProxyLowConfidence = confidence != null && confidence !== 'high';
      const isMultiResultNoExactMatch = locationResults.length > 1
          && indexOfValueInArrayByProperty(locationResults, "formatted_address", locationVal) < 0;
      // Call entry previews only what a dispatcher can answer: a discrepancy against a stored
      // coordinate, or a form that demands a confirmed pin (measured: zero pin corrections in
      // 77 low-confidence modal encounters).
      const shouldShowPreview = proxyEnabled && (
          discrepancyFromPrevious
          || requireConfirm
          || (!acceptApproximate && (isProxyLowConfidence || isMultiResultNoExactMatch))
      );

      // Call-entry proxy path, no preview owed — accept without asking.
      if (proxyEnabled && acceptApproximate && !shouldShowPreview && isProxyLowConfidence) {
        // Same semantics as the preview's passive accept below: keep the USER's typed text
        // (low-confidence geocoder text has rewritten records with cross-city guesses), take
        // the pin as best-available, land LOW so Address Review and the map flag can see it.
        hideLoadScreenIfParamTrue(showLoadingScreen);
        App.showPopupNotification('Address could not be verified. The call location is approximate.', 'warning');
        resolve(buildUseAsTypedResultsObj(locationVal, topLat, topLng));
        return;
      }
      if (proxyEnabled && acceptApproximate && !shouldShowPreview && isMultiResultNoExactMatch) {
        // High confidence with several candidates: take the geocoder's own top ranking
        // silently. The premise holds here too — a dispatcher cannot adjudicate between
        // candidates they cannot verify.
        hideLoadScreenIfParamTrue(showLoadingScreen);
        resultsObj.geocoderResults = [top];
        sendGeocodeSample(locationVal, resultsObj.geocoderResults[0]);
        resolve(resultsObj);
        return;
      }

      // Multi-result handling — branches based on proxy/non-proxy.
      if (locationResults.length > 1 && !shouldShowPreview) {
        const indexOfLocationObj: number = indexOfValueInArrayByProperty(locationResults, "formatted_address", locationVal);
        if (indexOfLocationObj >= 0) {
          // Exact match found — use it silently (same as pre-migration behavior).
          resultsObj.geocoderResults = [resultsObj.geocoderResults[indexOfLocationObj]];
          hideLoadScreenIfParamTrue(showLoadingScreen);
          sendGeocodeSample(locationVal, resultsObj.geocoderResults[0]);
          resolve(resultsObj);
          return;
        }

        // Non-pilot path: fall back to the legacy AddressSelectionModal text-list picker.
        if (!proxyEnabled) {
          hideLoadScreenIfParamTrue(showLoadingScreen);
          try {
            const selectedIdx = await showAddressSelectionModal(
              locationResults.map((r: any) => r.formatted_address)
            );
            resultsObj.geocoderResults = [resultsObj.geocoderResults[selectedIdx]];
            sendGeocodeSample(locationVal, resultsObj.geocoderResults[0]);
            resolve(resultsObj);
          } catch (e) {
            reject(e);
          }
          return;
        }
        // Proxy path never reaches here: multi-result-no-exact-match is accepted silently in
        // the call-entry branch above (or owed a preview via discrepancy/requireConfirm, in
        // which case this block was skipped entirely).
      }

      if (shouldShowPreview) {
        hideLoadScreenIfParamTrue(showLoadingScreen);

        // Build alternates from index >= 1 (top result is shown separately in the preview)
        const alternates = locationResults.slice(1).map((r: any) => {
          const lat = typeof r.geometry?.location?.lat === 'function'
              ? r.geometry.location.lat()
              : r.geometry?.location?.lat;
          const lng = typeof r.geometry?.location?.lng === 'function'
              ? r.geometry.location.lng()
              : r.geometry?.location?.lng;
          return {
            formatted: r.formatted_address ?? '',
            lat,
            lng,
            accuracyType: (r as any).accuracyType ?? undefined,
          };
        });

        let mode: 'low-confidence' | 'no-result' | 'discrepancy' = 'low-confidence';
        if (discrepancyFromPrevious) mode = 'discrepancy';

        try {
          const companyCenter = await fetchCompanyMapCenter(params.companyId);
          // Write-back is only offered in low-confidence mode (never discrepancy — that's about a
          // saved coordinate the user is confirming, not a fresh client/site fix). No permission
          // gate beyond that (owner decision 2026-07-16): ROLE_CLIENT_MANAGEMENT only exists in
          // Admin-module sessions and this modal only appears on CAD/MDT screens, so gating on it
          // made the offer unreachable. The pin write is narrow (one coordinate, MANUAL provenance,
          // audited) and the backend authorizes it like any client update.
          const canWriteBack = mode !== 'discrepancy';
          const writeBackTarget = canWriteBack
            ? await getWriteBackTargetForAddress(locationVal, params.companyId, params.selectedClientId ?? null)
            : undefined;
          const decision = await showGeocodePreview({
            mode,
            formatted: top?.formatted_address,
            lat: topLat,
            lng: topLng,
            accuracyType: (resultsObj as any).accuracyType ?? undefined,
            country: extractCountryCode(top),
            query: locationVal,
            alternates,
            previousLat: params.previousLat,
            previousLng: params.previousLng,
            fallbackCenterLat: companyCenter.lat,
            fallbackCenterLng: companyCenter.lng,
            requireConfirm,
            writeBackTarget,
            canWriteBack,
          });

          if (decision.reverted) {
            // Discrepancy mode "Keep Previous Location" — short-circuit, propagate a marker so
            // the caller doesn't overwrite stored coords.
            reject(new Error('keep-previous-location'));
            return;
          }

          // "Use address as typed" — keep the typed text, best-available coord, land LOW.
          if (decision.useAsTyped) {
            hideLoadScreenIfParamTrue(showLoadingScreen);
            resolve(buildUseAsTypedResultsObj(locationVal, decision.lat, decision.lng));
            return;
          }

          // Write-back offer: a corrected OR confirmed pin on a known-but-untrusted client/site record.
          if (decision.writeBack && writeBackTarget && hasNumericValue(decision.lat) && hasNumericValue(decision.lng)) {
            fireLocationPinWriteBack(writeBackTarget, decision.lat, decision.lng, params.companyId);
          }

          // If the user picked an alternate or dragged the pin, swap the top result accordingly
          // and tag the resultsObj so downstream code stores MANUAL instead of LOW.
          if (decision.alternateIndex >= 0) {
            resultsObj.geocoderResults = [locationResults[decision.alternateIndex + 1]];
            (resultsObj as any).userAdjusted = true;
          } else if (decision.lat !== topLat || decision.lng !== topLng) {
            // User dragged the pin — synthesize a result at the new coords
            const draggedResult = {
              ...top,
              formatted_address: decision.formatted || top?.formatted_address,
              geometry: {
                location: {
                  lat: () => decision.lat,
                  lng: () => decision.lng,
                },
              },
            };
            resultsObj.geocoderResults = [draggedResult];
            (resultsObj as any).userAdjusted = true;
          } else if (confidence === 'low') {
            // Passive Accept of a low-confidence guess: keep the USER's typed text, take the
            // pin as best-available. Adopting the geocoder's formatted text here rewrote
            // client records with fuzzy cross-city guesses ("3900 Oak Rd, Modesto" stored as
            // "3900 Oak View Ct, Turlock") — geocoder text is only trustworthy normalization
            // when the match is high-confidence. Record still lands LOW for Address Review.
            resolve(buildUseAsTypedResultsObj(locationVal, decision.lat, decision.lng));
            return;
          } else {
            resultsObj.geocoderResults = [top];
          }
          sendGeocodeSample(locationVal, resultsObj.geocoderResults[0]);
          resolve(resultsObj);
        } catch (e) {
          reject(e);
        }
        return;
      }

      hideLoadScreenIfParamTrue(showLoadingScreen);
      sendGeocodeSample(locationVal, resultsObj.geocoderResults[0]);
      resolve(resultsObj);
    }, geocodeContext);
  });
}
